import React, { useState } from 'react';
import { Patient, ActionPlan } from '../types/patient';
import { Target, Heart, Moon, Activity, Apple, TrendingUp, Clock, CheckCircle2, Plus, X, Minus } from 'lucide-react';
import { getBadgeClasses } from '../utils/styleHelpers';
import Modal from './Modal';

interface HealthPlansProps {
  patient: Patient;
  onPlanAdded?: (patientId: string, plan: ActionPlan) => void;
}

interface HealthPlan {
  id: string;
  name: string;
  description: string;
  icon: any;
  iconColor: string;
  status: 'active' | 'completed' | 'in-progress';
  progress: number;
  goals: string[];
  timeline: string;
  startDate: string;
  metrics: {
    label: string;
    current: string;
    target: string;
  }[];
}

// Icon mapping for rendering
const ICON_OPTIONS = [
  { name: 'Activity', component: Activity },
  { name: 'Heart', component: Heart },
  { name: 'Moon', component: Moon },
  { name: 'Apple', component: Apple },
  { name: 'TrendingUp', component: TrendingUp },
  { name: 'Target', component: Target },
];

// Color options for plan icons
const COLOR_OPTIONS = [
  { value: 'bg-success-100 text-success-600', label: 'Green', preview: 'bg-success-500' },
  { value: 'bg-danger-100 text-danger-600', label: 'Red', preview: 'bg-danger-500' },
  { value: 'bg-info-100 text-info-600', label: 'Blue', preview: 'bg-info-500' },
  { value: 'bg-warning-100 text-warning-600', label: 'Yellow', preview: 'bg-warning-500' },
  { value: 'bg-brand-beige-200 text-brand-dark-600', label: 'Beige', preview: 'bg-brand-beige-400' },
];

// Preset plan templates
const PRESET_TEMPLATES: Record<string, Partial<ActionPlan>> = {
  'weight-loss': {
    name: 'Weight Loss Plan',
    description: 'Achieve and maintain a healthy weight through balanced nutrition and regular physical activity.',
    icon: 'Activity',
    iconColor: 'bg-success-100 text-success-600',
    goals: [
      'Reduce weight by 15 lbs',
      'Exercise 150 minutes per week',
      'Track daily caloric intake',
      'Maintain balanced macronutrients',
    ],
    timeline: '3 months',
    metrics: [
      { label: 'Weight', current: '', target: '170 lbs' },
      { label: 'Weekly Exercise', current: '', target: '150 min' },
      { label: 'Daily Calories', current: '', target: '1,800' },
    ],
  },
  'blood-sugar': {
    name: 'Blood Sugar Management',
    description: 'Maintain optimal glucose levels through diet, exercise, and medication adherence.',
    icon: 'Heart',
    iconColor: 'bg-danger-100 text-danger-600',
    goals: [
      'Keep fasting glucose below 100 mg/dL',
      'Monitor blood sugar 3x daily',
      'Follow low-glycemic diet',
      'Take medications as prescribed',
    ],
    timeline: '6 months',
    metrics: [
      { label: 'Fasting Glucose', current: '', target: '<100 mg/dL' },
      { label: 'HbA1c', current: '', target: '<6.0%' },
      { label: 'Medication Adherence', current: '', target: '100%' },
    ],
  },
  'sleep-quality': {
    name: 'Sleep Quality Improvement',
    description: 'Enhance sleep duration and quality through better sleep hygiene and consistent routines.',
    icon: 'Moon',
    iconColor: 'bg-info-100 text-info-600',
    goals: [
      'Sleep 7-8 hours nightly',
      'Establish consistent sleep schedule',
      'Reduce screen time before bed',
      'Create relaxing bedtime routine',
    ],
    timeline: '2 months',
    metrics: [
      { label: 'Avg Sleep Duration', current: '', target: '7.5 hrs' },
      { label: 'Sleep Quality Score', current: '', target: '80/100' },
      { label: 'Wake Episodes', current: '', target: '<2' },
    ],
  },
};

export default function HealthPlans({ patient, onPlanAdded }: HealthPlansProps) {
  // State for locally created plans (during this session)
  const [localPlans, setLocalPlans] = useState<ActionPlan[]>([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'selection' | 'form'>('selection');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<ActionPlan>>({
    status: 'active',
    progress: 0,
    goals: [''],
    metrics: [{ label: '', current: '', target: '' }],
    icon: 'Target',
    iconColor: 'bg-brand-beige-200 text-brand-dark-600',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Get plans from patient data + locally created plans
  const allPlans = [...(patient.actionPlans || []), ...localPlans];

  // Convert ActionPlan to HealthPlan format with icon components
  const healthPlans: HealthPlan[] = allPlans.map(plan => ({
    ...plan,
    icon: ICON_OPTIONS.find(opt => opt.name === plan.icon)?.component || Target,
  }));

  const activePlans = healthPlans.filter(p => p.status !== 'completed');
  const completedPlans = healthPlans.filter(p => p.status === 'completed');

  // Handler: Select a preset or start custom plan
  const handlePresetSelect = (presetKey: string | null) => {
    setSelectedPreset(presetKey);
    if (presetKey && PRESET_TEMPLATES[presetKey]) {
      const template = PRESET_TEMPLATES[presetKey];
      setFormData({
        ...template,
        status: 'active',
        progress: 0,
        startDate: new Date().toISOString().split('T')[0],
      });
    } else {
      // Custom plan - reset to defaults
      setFormData({
        status: 'active',
        progress: 0,
        goals: [''],
        metrics: [{ label: '', current: '', target: '' }],
        icon: 'Target',
        iconColor: 'bg-brand-beige-200 text-brand-dark-600',
      });
    }
    setModalStep('form');
    setFormErrors({});
  };

  // Handler: Close modal and reset state
  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalStep('selection');
    setSelectedPreset(null);
    setFormData({
      status: 'active',
      progress: 0,
      goals: [''],
      metrics: [{ label: '', current: '', target: '' }],
      icon: 'Target',
      iconColor: 'bg-brand-beige-200 text-brand-dark-600',
    });
    setFormErrors({});
  };

  // Handler: Submit form and create plan
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const errors: Record<string, string> = {};
    if (!formData.name?.trim()) errors.name = 'Plan name is required';
    if (!formData.description?.trim()) errors.description = 'Description is required';
    if (!formData.timeline?.trim()) errors.timeline = 'Timeline is required';
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.goals || formData.goals.filter(g => g.trim()).length === 0) {
      errors.goals = 'At least one goal is required';
    }
    if (!formData.metrics || formData.metrics.filter(m => m.label.trim()).length === 0) {
      errors.metrics = 'At least one metric is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const newPlan: ActionPlan = {
      id: `plan-${Date.now()}`,
      patientId: patient.id,
      name: formData.name!,
      description: formData.description!,
      icon: formData.icon || 'Target',
      iconColor: formData.iconColor || 'bg-brand-beige-200 text-brand-dark-600',
      status: formData.status || 'active',
      progress: formData.progress || 0,
      goals: formData.goals!.filter(g => g.trim()),
      timeline: formData.timeline!,
      startDate: formData.startDate!,
      metrics: formData.metrics!.filter(m => m.label.trim()),
    };

    if (onPlanAdded) {
      onPlanAdded(patient.id, newPlan);
    } else {
      setLocalPlans(prev => [...prev, newPlan]);
    }

    handleModalClose();
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') return getBadgeClasses('low', 'sm');
    if (status === 'in-progress') return getBadgeClasses('medium', 'sm');
    return getBadgeClasses('high', 'sm');
  };

  const getStatusLabel = (status: string) => {
    if (status === 'active') return 'Active';
    if (status === 'in-progress') return 'In Progress';
    return 'Completed';
  };

  // Preset Selection Step Component
  const PresetSelectionStep = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Weight Loss Preset */}
      <button
        onClick={() => handlePresetSelect('weight-loss')}
        className="card text-left hover:shadow-lg hover:border-brand-dark-300 transition-all cursor-pointer border-2 border-transparent p-6"
      >
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-3 rounded-lg bg-success-100 text-success-600">
            <Activity className="w-6 h-6" />
          </div>
          <h4 className="font-semibold text-brand-dark-600">Weight Loss Plan</h4>
        </div>
        <p className="text-sm text-brand-taupe-600">Balanced nutrition and physical activity</p>
      </button>

      {/* Blood Sugar Preset */}
      <button
        onClick={() => handlePresetSelect('blood-sugar')}
        className="card text-left hover:shadow-lg hover:border-brand-dark-300 transition-all cursor-pointer border-2 border-transparent p-6"
      >
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-3 rounded-lg bg-danger-100 text-danger-600">
            <Heart className="w-6 h-6" />
          </div>
          <h4 className="font-semibold text-brand-dark-600">Blood Sugar Management</h4>
        </div>
        <p className="text-sm text-brand-taupe-600">Maintain optimal glucose levels</p>
      </button>

      {/* Sleep Quality Preset */}
      <button
        onClick={() => handlePresetSelect('sleep-quality')}
        className="card text-left hover:shadow-lg hover:border-brand-dark-300 transition-all cursor-pointer border-2 border-transparent p-6"
      >
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-3 rounded-lg bg-info-100 text-info-600">
            <Moon className="w-6 h-6" />
          </div>
          <h4 className="font-semibold text-brand-dark-600">Sleep Quality Improvement</h4>
        </div>
        <p className="text-sm text-brand-taupe-600">Better sleep hygiene and routines</p>
      </button>

      {/* Custom Plan */}
      <button
        onClick={() => handlePresetSelect(null)}
        className="card text-left hover:shadow-lg hover:border-brand-dark-300 transition-all cursor-pointer border-2 border-transparent p-6"
      >
        <div className="flex items-center space-x-3 mb-3">
          <div className="p-3 rounded-lg bg-brand-beige-200 text-brand-dark-600">
            <Plus className="w-6 h-6" />
          </div>
          <h4 className="font-semibold text-brand-dark-600">Custom Plan</h4>
        </div>
        <p className="text-sm text-brand-taupe-600">Create your own personalized plan</p>
      </button>
    </div>
  );

  // Plan Form Step Component
  const PlanFormStep = () => {
    const updateGoal = (index: number, value: string) => {
      const updated = [...(formData.goals || [])];
      updated[index] = value;
      setFormData({ ...formData, goals: updated });
    };

    const addGoal = () => {
      setFormData({ ...formData, goals: [...(formData.goals || []), ''] });
    };

    const removeGoal = (index: number) => {
      setFormData({ ...formData, goals: (formData.goals || []).filter((_, i) => i !== index) });
    };

    const updateMetric = (index: number, field: 'label' | 'current' | 'target', value: string) => {
      const updated = [...(formData.metrics || [])];
      updated[index] = { ...updated[index], [field]: value };
      setFormData({ ...formData, metrics: updated });
    };

    const addMetric = () => {
      setFormData({ ...formData, metrics: [...(formData.metrics || []), { label: '', current: '', target: '' }] });
    };

    const removeMetric = (index: number) => {
      setFormData({ ...formData, metrics: (formData.metrics || []).filter((_, i) => i !== index) });
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-brand-dark-600 mb-2">
            Plan Name <span className="text-danger-600">*</span>
          </label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg ${formErrors.name ? 'border-danger-500' : 'border-brand-taupe-300'}`}
            placeholder="e.g., Weight Loss Plan"
          />
          {formErrors.name && <p className="text-danger-600 text-sm mt-1">{formErrors.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-brand-dark-600 mb-2">
            Description <span className="text-danger-600">*</span>
          </label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg ${formErrors.description ? 'border-danger-500' : 'border-brand-taupe-300'}`}
            rows={3}
            placeholder="Brief description of the plan"
          />
          {formErrors.description && <p className="text-danger-600 text-sm mt-1">{formErrors.description}</p>}
        </div>

        {/* Icon and Color */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">Icon</label>
            <select
              value={formData.icon || 'Target'}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              className="w-full px-3 py-2 border border-brand-taupe-300 rounded-lg"
            >
              {ICON_OPTIONS.map(opt => (
                <option key={opt.name} value={opt.name}>{opt.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">Color</label>
            <select
              value={formData.iconColor || 'bg-brand-beige-200 text-brand-dark-600'}
              onChange={(e) => setFormData({ ...formData, iconColor: e.target.value })}
              className="w-full px-3 py-2 border border-brand-taupe-300 rounded-lg"
            >
              {COLOR_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Timeline and Start Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">
              Timeline <span className="text-danger-600">*</span>
            </label>
            <input
              type="text"
              value={formData.timeline || ''}
              onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${formErrors.timeline ? 'border-danger-500' : 'border-brand-taupe-300'}`}
              placeholder="e.g., 3 months"
            />
            {formErrors.timeline && <p className="text-danger-600 text-sm mt-1">{formErrors.timeline}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">
              Start Date <span className="text-danger-600">*</span>
            </label>
            <input
              type="date"
              value={formData.startDate || ''}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${formErrors.startDate ? 'border-danger-500' : 'border-brand-taupe-300'}`}
            />
            {formErrors.startDate && <p className="text-danger-600 text-sm mt-1">{formErrors.startDate}</p>}
          </div>
        </div>

        {/* Status and Progress */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">Status</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="active"
                  checked={formData.status === 'active'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="mr-2"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="in-progress"
                  checked={formData.status === 'in-progress'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="mr-2"
                />
                <span className="text-sm">In Progress</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-dark-600 mb-2">
              Progress: {formData.progress || 0}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.progress || 0}
              onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>
        </div>

        {/* Goals */}
        <div>
          <label className="block text-sm font-medium text-brand-dark-600 mb-2">
            Goals <span className="text-danger-600">*</span>
          </label>
          <div className="space-y-2">
            {(formData.goals || ['']).map((goal, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => updateGoal(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-brand-taupe-300 rounded-lg"
                  placeholder="Enter a goal"
                />
                {(formData.goals || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGoal(index)}
                    className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addGoal}
            className="mt-2 text-sm text-brand-dark-500 hover:text-brand-dark-600 flex items-center space-x-1"
          >
            <Plus className="w-4 h-4" />
            <span>Add Goal</span>
          </button>
          {formErrors.goals && <p className="text-danger-600 text-sm mt-1">{formErrors.goals}</p>}
        </div>

        {/* Metrics */}
        <div>
          <label className="block text-sm font-medium text-brand-dark-600 mb-2">
            Metrics <span className="text-danger-600">*</span>
          </label>
          <div className="space-y-3">
            {(formData.metrics || [{ label: '', current: '', target: '' }]).map((metric, index) => (
              <div key={index} className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={metric.label}
                  onChange={(e) => updateMetric(index, 'label', e.target.value)}
                  className="px-3 py-2 border border-brand-taupe-300 rounded-lg"
                  placeholder="Label"
                />
                <input
                  type="text"
                  value={metric.current}
                  onChange={(e) => updateMetric(index, 'current', e.target.value)}
                  className="px-3 py-2 border border-brand-taupe-300 rounded-lg"
                  placeholder="Current"
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={metric.target}
                    onChange={(e) => updateMetric(index, 'target', e.target.value)}
                    className="flex-1 px-3 py-2 border border-brand-taupe-300 rounded-lg"
                    placeholder="Target"
                  />
                  {(formData.metrics || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMetric(index)}
                      className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addMetric}
            className="mt-2 text-sm text-brand-dark-500 hover:text-brand-dark-600 flex items-center space-x-1"
          >
            <Plus className="w-4 h-4" />
            <span>Add Metric</span>
          </button>
          {formErrors.metrics && <p className="text-danger-600 text-sm mt-1">{formErrors.metrics}</p>}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-4 py-2 border border-brand-taupe-300 text-brand-dark-600 rounded-lg hover:bg-brand-beige-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
          >
            Create Plan
          </button>
        </div>
      </form>
    );
  };

  const PlanCard = ({ plan }: { plan: HealthPlan }) => {
    const Icon = plan.icon;
    return (
      <div className="card">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-3 rounded-lg ${plan.iconColor}`}>
              <Icon className="w-6 h-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-brand-dark-600">{plan.name}</h3>
              <p className="text-sm text-brand-taupe-600">{plan.description}</p>
            </div>
          </div>
          <div className={getStatusBadge(plan.status)}>
            {plan.status === 'completed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
            <span className="capitalize">{getStatusLabel(plan.status)}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-brand-dark-600">Overall Progress</span>
            <span className="text-sm font-bold text-brand-dark-600">{plan.progress}%</span>
          </div>
          <div className="w-full bg-brand-beige-200 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                plan.progress === 100
                  ? 'bg-success-500'
                  : plan.progress >= 70
                  ? 'bg-success-400'
                  : plan.progress >= 40
                  ? 'bg-warning-400'
                  : 'bg-danger-400'
              }`}
              style={{ width: `${plan.progress}%` }}
            />
          </div>
        </div>

        {/* Timeline and Start Date */}
        <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-brand-taupe-200">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-brand-taupe-500" aria-hidden="true" />
            <span className="text-sm text-brand-taupe-600">
              <span className="font-medium text-brand-dark-600">{plan.timeline}</span> duration
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Target className="w-4 h-4 text-brand-taupe-500" aria-hidden="true" />
            <span className="text-sm text-brand-taupe-600">
              Started <span className="font-medium text-brand-dark-600">{new Date(plan.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-brand-dark-600 mb-3">Key Metrics</h4>
          <div className="grid grid-cols-3 gap-3">
            {plan.metrics.map((metric, idx) => (
              <div key={idx} className="bg-brand-ivory-100 rounded-lg p-3 text-center">
                <div className="text-xs text-brand-taupe-600 mb-1">{metric.label}</div>
                <div className="text-sm font-bold text-brand-dark-600">{metric.current}</div>
                <div className="text-xs text-brand-taupe-500">Goal: {metric.target}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Goals */}
        <div>
          <h4 className="text-sm font-semibold text-brand-dark-600 mb-2">Goals</h4>
          <ul className="space-y-2">
            {plan.goals.map((goal, idx) => (
              <li key={idx} className="flex items-start space-x-2">
                <CheckCircle2 className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-sm text-brand-taupe-600">{goal}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Target className="w-6 h-6 text-brand-dark-500" />
          <h2 className="text-2xl font-bold text-brand-dark-600">Health Plans</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Plan</span>
          </button>
          <div className="text-sm text-brand-taupe-600">
            <span className="font-bold text-brand-dark-600">{activePlans.length}</span> active plans
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card bg-gradient-to-br from-brand-ivory-100 to-brand-beige-100">
        <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">Plans Overview</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-bold text-success-700">{activePlans.length}</div>
            <div className="text-sm text-brand-taupe-600">Active Plans</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-warning-700">{healthPlans.filter(p => p.status === 'in-progress').length}</div>
            <div className="text-sm text-brand-taupe-600">In Progress</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-brand-dark-600">{completedPlans.length}</div>
            <div className="text-sm text-brand-taupe-600">Completed</div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {healthPlans.length === 0 && (
        <div className="card text-center py-12">
          <Target className="w-12 h-12 text-brand-taupe-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-brand-dark-600 mb-2">No action plans yet</h3>
          <p className="text-brand-taupe-600 mb-4">Create a plan to start tracking health goals.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create First Plan</span>
          </button>
        </div>
      )}

      {/* Active Plans */}
      {activePlans.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">Active Plans</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activePlans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Plans */}
      {completedPlans.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">Completed Plans</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {completedPlans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        </div>
      )}

      {/* Create Plan Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={modalStep === 'selection' ? 'Create New Plan' : 'Plan Details'}
        size="xl"
      >
        {modalStep === 'selection' ? <PresetSelectionStep /> : <PlanFormStep />}
      </Modal>
    </div>
  );
}